import { useCallback, useState } from 'react';
import { Product } from '@axo/shared/data-access/types';
import { useLoanApplication } from '@axo/shared/data-access/hooks/queries/useLoanApplication.v2';
import { Button } from '@blueprintjs/core';
import { useAceProductRecommendationReapply } from '../../hooks/utils/useAceProductRecommendationReapply';
import { CreditLimitDialog } from './CreditLimitDialog';
import { resolveCreditCardAmountLimits } from './constants';

interface CreateRecommendationItemButtonProps {
  applicationID: string;
  recommendationProduct: Product;
}

export const CreateRecommendationItemButton: React.FC<
  CreateRecommendationItemButtonProps
> = ({ applicationID, recommendationProduct }) => {
  const { reapply, isReapplying } = useAceProductRecommendationReapply({
    applicationID,
    product: recommendationProduct,
  });

  const { data: applicationData } = useLoanApplication({ id: applicationID });
  const limits = resolveCreditCardAmountLimits(applicationData?.MarketCountry);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (recommendationProduct === Product.CreditCard) {
      setDialogOpen(true);
    } else {
      reapply();
    }
  }, [recommendationProduct, reapply]);

  const handleConfirm = useCallback(
    (appliedAmount: number) => {
      setDialogOpen(false);
      reapply(appliedAmount);
    },
    [reapply]
  );

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <>
      <Button
        type="button"
        onClick={handleClick}
        icon="plus"
        loading={isReapplying}
        intent="success"
      >
        Add
      </Button>
      <CreditLimitDialog
        isOpen={dialogOpen}
        limits={limits}
        currency={applicationData?.Currency}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};
